"use client";

/**
 * GeoFlywheel - animated "The GEO Flywheel" infographic for blog content.
 *
 * Ported pixel-faithfully from the Claude Design handoff
 * (GEO Flywheel Animation, 1080×1350 poster). The original design is a fixed
 * pixel canvas; here we render that exact canvas and scale it to the article
 * column width with a ResizeObserver so it stays responsive without distortion.
 *
 * Animations live in app/globals.css (gfw-* keyframes + helper classes) and are
 * disabled under prefers-reduced-motion.
 *
 * Fonts are mapped to the site's self-hosted families: DM Sans (display, ≤600)
 * + Poppins (labels/subtitle, ≤500) instead of the design's Google Fonts CDN.
 */

import { useEffect, useRef, useState } from "react";

const BASE_W = 1080;
const BASE_H = 1350;

const BLUE = "#3B82F6";
const PALE = "#AED1FF";
const YELLOW = "#EEF975";

const DM = '"DM Sans", sans-serif';
const POP = "Poppins, sans-serif";

// bottom (1) → top (8). Each card lifts higher up-and-right than the last.
const CARDS = [
  { n: 1, label: "Helpful Content", left: 70, top: 1170 },
  { n: 2, label: "Better Entity Understanding", left: 126, top: 1048 },
  { n: 3, label: "More Trust Signals", left: 182, top: 926 },
  { n: 4, label: "More Citations & Brand Mentions", left: 238, top: 804 },
  { n: 5, label: "Higher AI Confidence", left: 294, top: 682 },
  { n: 6, label: "More AI Recommendations", left: 350, top: 560 },
  { n: 7, label: "More AI Referral Traffic", left: 406, top: 438 },
  { n: 8, label: "Greater Brand Authority", left: 462, top: 316 },
];

export default function GeoFlywheel() {
  const boxRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const update = () => setScale(el.clientWidth / BASE_W);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <figure className="not-prose my-10">
      <div
        ref={boxRef}
        className="relative w-full overflow-hidden rounded-2xl border border-nws-grey-medium"
        style={{ aspectRatio: `${BASE_W} / ${BASE_H}` }}
        role="img"
        aria-label="The GEO Flywheel: helpful content leads to better entity understanding, more trust signals, more citations and brand mentions, higher AI confidence, more AI recommendations, more AI referral traffic, and greater brand authority - then the loop repeats, starting higher each turn."
      >
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: BASE_W,
            height: BASE_H,
            background: "#faf9f7",
            transformOrigin: "top left",
            // Start at scale(0) so the unscaled 1080px canvas never flashes before
            // the ResizeObserver measures the column width. No transition (snaps).
            transform: `scale(${scale})`,
          }}
        >
          {/* back layer: growth line + return loop */}
          <svg
            width={BASE_W}
            height={BASE_H}
            viewBox={`0 0 ${BASE_W} ${BASE_H}`}
            fill="none"
            style={{ position: "absolute", inset: 0, overflow: "visible" }}
          >
            <defs>
              <linearGradient id="gfwGrow" x1="0" y1="1" x2="1" y2="0">
                <stop offset="0" stopColor={PALE} />
                <stop offset="1" stopColor={BLUE} />
              </linearGradient>
              <path id="gfwGrowthline" d="M500,1208 L892,354" />
            </defs>
            <use
              href="#gfwGrowthline"
              stroke="#e7ecf5"
              strokeWidth={20}
              strokeLinecap="round"
            />
            <use
              href="#gfwGrowthline"
              stroke="url(#gfwGrow)"
              strokeWidth={5}
              strokeLinecap="round"
              strokeDasharray="13 12"
              className="gfw-growth-dash"
            />
            {/* return loop (left) */}
            <path
              d="M462,354 C 70,520 70,1010 162,1205"
              stroke={PALE}
              strokeWidth={5}
              strokeLinecap="round"
              strokeDasharray="15 13"
              className="gfw-return-dash"
            />
            <path
              d="M462,354 C 70,520 70,1010 162,1205"
              stroke={BLUE}
              strokeWidth={5}
              strokeLinecap="round"
              className="gfw-return-pulse"
            />
            <polygon points="162,1205 150,1181 178,1187" fill={BLUE} />
          </svg>

          {/* ascending cards (1 bottom → 8 top) */}
          {CARDS.map((c) => {
            const isGoal = c.n === 8;
            return (
              <div
                key={c.n}
                className="gfw-card"
                style={{
                  boxSizing: "border-box",
                  position: "absolute",
                  left: c.left,
                  top: c.top,
                  width: 430,
                  height: 76,
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  padding: "0 22px",
                  background: "#fff",
                  border: "1.5px solid #e8e6e1",
                  borderRadius: 18,
                  boxShadow: "0 2px 10px rgba(20,20,20,.05)",
                  animationDelay: `${c.n - 1}s`,
                }}
              >
                <div
                  className="gfw-badge"
                  style={{
                    flexShrink: 0,
                    width: 42,
                    height: 42,
                    borderRadius: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: DM,
                    fontWeight: 600,
                    fontSize: 19,
                    background: isGoal ? YELLOW : PALE,
                    color: isGoal ? "#1a1a00" : "#0b2c66",
                    animationDelay: `${c.n - 1}s`,
                  }}
                >
                  {c.n}
                </div>
                <div
                  style={{
                    fontFamily: POP,
                    fontWeight: 600,
                    fontSize: 20,
                    lineHeight: 1.1,
                  }}
                >
                  {c.label}
                </div>
              </div>
            );
          })}

          {/* front layer: rising glow dot threading the cards' right edges */}
          <div
            className="gfw-dot"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: 30,
              height: 30,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, #EEF975 0%, #EEF975 22%, #3B82F6 42%, rgba(59,130,246,0.55) 62%, rgba(59,130,246,0) 100%)",
              boxShadow: "0 0 18px 4px rgba(59,130,246,.45)",
              offsetPath: "path('M500,1208 L892,354')",
              offsetAnchor: "50% 50%",
              pointerEvents: "none",
            }}
          />

          {/* loop label */}
          <div
            style={{
              position: "absolute",
              left: 58,
              top: 688,
              width: 150,
              textAlign: "center",
              fontFamily: DM,
              fontWeight: 600,
              fontSize: 13,
              letterSpacing: ".14em",
              textTransform: "uppercase",
              color: BLUE,
              lineHeight: 1.5,
            }}
          >
            Loop back
            <br />
            starts higher
          </div>

          {/* title */}
          <div style={{ position: "absolute", left: 60, top: 84, width: 780 }}>
            <div
              style={{
                fontFamily: DM,
                fontWeight: 600,
                fontSize: 14,
                letterSpacing: ".22em",
                textTransform: "uppercase",
                color: BLUE,
              }}
            >
              Generative Engine Optimization
            </div>
            <div
              style={{
                margin: "12px 0 0",
                fontFamily: DM,
                fontWeight: 600,
                fontSize: 62,
                lineHeight: 1,
                color: "#111",
                letterSpacing: "-.02em",
              }}
            >
              The GEO Flywheel
            </div>
            <p
              style={{
                margin: "16px 0 0",
                fontFamily: POP,
                fontWeight: 400,
                fontSize: 21,
                lineHeight: 1.4,
                color: "#5b5b58",
                maxWidth: 600,
              }}
            >
              Every signal lifts the next - so each turn of the loop{" "}
              <span style={{ color: "#111", fontWeight: 600 }}>
                starts higher than the last.
              </span>
            </p>
          </div>
        </div>
      </div>
    </figure>
  );
}
